import React from 'react';
import Styled from 'styled-components/macro';
import {PopoverHandler} from '@forecast-it/design-system';
import ForecastTooltip from '../tooltips/ForecastTooltip';
import moreHorizontalIcon from '../../../../images/components/overlay/navigation-show-more-horizontal-black.svg';

// sky/comp/button/color/border/midemphasismonochrome/default
const COLOR_BUTTON_BORDER = '#6b6d7b';
// sky/sys/color/border/enabled
const COLOR_BORDER_ENABLED = '#6b6d7b';
// sky/sys/color/text/content/main
const COLOR_TEXT_MAIN = '#232334';
// sky/sys/color/text/content/disabled
const COLOR_TEXT_DISABLED = '#a1a1ad';

const MoreButton = Styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	padding: 0 12px;
	border: 1px solid ${COLOR_BUTTON_BORDER};
	border-radius: 8px;
	background: transparent;
	cursor: pointer;
	&:hover {
		background-color: rgba(0, 0, 0, 0.04);
	}
`;

const DropdownMenuShell = Styled.ul`
	list-style: none;
	margin: 0;
	padding: 8px 0;
	background: #ffffff;
	border: 1px solid ${COLOR_BORDER_ENABLED};
	border-radius: 8px;
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.12);
	min-width: 160px;
`;

const DropdownMenuItemStyled = Styled.li<{$disabled?: boolean}>`
	display: flex;
	align-items: center;
	min-height: 44px;
	padding: 0 16px;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.15px;
	color: ${({$disabled}) => ($disabled ? COLOR_TEXT_DISABLED : COLOR_TEXT_MAIN)};
	cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'pointer')};
	&:hover {
		background-color: ${({$disabled}) => ($disabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)')};
	}
`;

type DropdownMenuItemProps = {
	children: React.ReactNode;
	onClick: () => void;
	/** When true, the item is greyed out and clicking does nothing. */
	disabled?: boolean;
	/** Optional tooltip shown on hover — typically used to explain a disabled state. */
	tooltip?: React.ReactNode;
	/** Optional test hook rendered as `data-cy` on the item. */
	dataCy?: string;
};

const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({children, onClick, disabled = false, tooltip, dataCy}) => {
	const {setOpen} = PopoverHandler.usePopoverState();
	const handleClick = () => {
		if (disabled) return;
		onClick();
		setOpen(false);
	};
	const content = tooltip ? (
		<ForecastTooltip
			content={tooltip}
			spanStyle={{display: 'flex', alignItems: 'center', alignSelf: 'stretch', width: '100%'}}
		>
			{children}
		</ForecastTooltip>
	) : (
		children
	);
	return (
		<DropdownMenuItemStyled $disabled={disabled} aria-disabled={disabled} data-cy={dataCy} onClick={handleClick}>
			{content}
		</DropdownMenuItemStyled>
	);
};

type DropdownMenuProps = {
	children: React.ReactNode;
	trigger?: React.ReactElement;
};

const DropdownMenu: React.FC<DropdownMenuProps> & {Item: typeof DropdownMenuItem} = ({children, trigger}) => {
	return (
		<PopoverHandler>
			<PopoverHandler.Trigger>
				{trigger ?? (
					<MoreButton aria-label="More actions">
						<img src={moreHorizontalIcon} alt="" width={20} height={20} />
					</MoreButton>
				)}
			</PopoverHandler.Trigger>
			<PopoverHandler.Content align="end" offset={4} showArrow={false}>
				<DropdownMenuShell>{children}</DropdownMenuShell>
			</PopoverHandler.Content>
		</PopoverHandler>
	);
};

DropdownMenu.Item = DropdownMenuItem;

export default DropdownMenu;
